import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

// Unread = messages from others (sender_user_id != current user) that are after the user's last_read_at for that thread.

async function getLastReadAt(userId: string, threadId: string): Promise<string | null> {
  const { data } = await supabase
    .from("user_thread_reads")
    .select("last_read_at")
    .eq("user_id", userId)
    .eq("thread_id", threadId)
    .maybeSingle();
  return data?.last_read_at ?? null;
}

// Get unread count for a specific thread (messages from others after last_read_at)
export const useUnreadCount = (threadId: string | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["unread-count", threadId],
    queryFn: async () => {
      if (!threadId) return 0;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const lastReadAt = await getLastReadAt(user.id, threadId);
      let q = supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("thread_id", threadId)
        .neq("sender_user_id", user.id);
      if (lastReadAt) {
        q = q.gt("created_at", lastReadAt);
      }
      const { count, error } = await q;

      if (error && (error.code === '400' || error.code === '404')) return 0;
      if (error) throw error;
      return count || 0;
    },
    enabled: !!threadId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Listen for new messages
  useEffect(() => {
    if (!threadId) return;

    const channel = supabase
      .channel(`unread-${threadId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `thread_id=eq.${threadId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["unread-count", threadId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, queryClient]);

  return query;
};

// Get total unread count across all threads for current user
export const useTotalUnreadCount = (enabled: boolean = true) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["total-unread-count"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      // Get all threads user participates in
      const { data: threads, error: threadsError } = await supabase
        .from("message_threads")
        .select("id");

      // Handle 400/404 as empty state (no threads = unread count = 0)
      if (threadsError && (threadsError.code === '400' || threadsError.code === '404' || threadsError.code === 'PGRST116')) {
        return 0;
      }
      if (threadsError) {
        // Only log non-expected errors
        console.error("[useTotalUnreadCount] Error fetching threads:", {
          message: threadsError.message,
          code: threadsError.code,
          details: threadsError.details,
        });
        return 0;
      }
      if (!threads) return 0;

      // Count only messages from others after last_read_at per thread
      let totalUnread = 0;

      for (const thread of threads) {
        const lastReadAt = await getLastReadAt(user.id, thread.id);
        let q = supabase
          .from("messages")
          .select("*", { count: "exact", head: true })
          .eq("thread_id", thread.id)
          .neq("sender_user_id", user.id);
        if (lastReadAt) {
          q = q.gt("created_at", lastReadAt);
        }
        const { count, error: countError } = await q;

        if (countError && (countError.code === '400' || countError.code === '404')) {
          continue;
        }
        if (countError) {
          console.error("[useTotalUnreadCount] Error counting messages:", {
            message: countError.message,
            code: countError.code,
          });
          continue;
        }

        totalUnread += count || 0;
      }

      return totalUnread;
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Listen for new messages globally
  useEffect(() => {
    const channel = supabase
      .channel("global-unread")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["total-unread-count"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return query;
};

// Mark thread as read: upsert user_thread_reads so unread counts drop
export const useMarkThreadAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("user_thread_reads")
        .upsert(
          { user_id: user.id, thread_id: threadId, last_read_at: new Date().toISOString() },
          { onConflict: "user_id,thread_id" }
        );
      if (error) throw error;
    },
    onSuccess: (_, threadId) => {
      queryClient.invalidateQueries({ queryKey: ["unread-count", threadId] });
      queryClient.invalidateQueries({ queryKey: ["total-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["task-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["property-unread-count"] });
    },
  });
};

// Get unread count for a task thread
export const useTaskUnreadCount = (taskId: string | undefined) => {
  return useQuery({
    queryKey: ["task-unread-count", taskId],
    queryFn: async () => {
      if (!taskId) return 0;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      // Get thread for task
      const { data: thread } = await supabase
        .from("message_threads")
        .select("id")
        .eq("task_id", taskId)
        .eq("type", "task")
        .maybeSingle();

      if (!thread) return 0;

      const lastReadAt = await getLastReadAt(user.id, thread.id);
      let q = supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("thread_id", thread.id)
        .neq("sender_user_id", user.id);
      if (lastReadAt) {
        q = q.gt("created_at", lastReadAt);
      }
      const { count, error } = await q;

      if (error && (error.code === '400' || error.code === '404')) return 0;
      if (error) throw error;
      return count || 0;
    },
    enabled: !!taskId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

// Get unread count for a property thread
export const usePropertyUnreadCount = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["property-unread-count", propertyId],
    queryFn: async () => {
      if (!propertyId) return 0;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      // Get thread for property
      const { data: thread } = await supabase
        .from("message_threads")
        .select("id")
        .eq("property_id", propertyId)
        .eq("type", "property")
        .maybeSingle();

      if (!thread) return 0;

      const lastReadAt = await getLastReadAt(user.id, thread.id);
      let q = supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("thread_id", thread.id)
        .neq("sender_user_id", user.id);
      if (lastReadAt) {
        q = q.gt("created_at", lastReadAt);
      }
      const { count, error } = await q;

      if (error && (error.code === '400' || error.code === '404')) return 0;
      if (error) throw error;
      return count || 0;
    },
    enabled: !!propertyId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};
